<template>
  <!-- 合同概况：金额/回款总览、状态分布、金额趋势、即将到期合同 -->
  <div class="contract-distribution">
    <list-page>
      <div v-loading="loading" class="stat-body">
        <!-- KPI -->
        <div class="stat-grid">
          <div class="stat-card">
            <div class="label">合同总数</div>
            <div class="value">{{ stat.totalCount || 0 }}<span class="unit">份</span></div>
            <div class="sub">全部合同</div>
          </div>
          <div class="stat-card">
            <div class="label">合同总金额</div>
            <div class="value warning">{{ money(stat.totalAmount) }}<span class="unit">元</span></div>
            <div class="sub">签约金额合计</div>
          </div>
          <div class="stat-card">
            <div class="label">已回款金额</div>
            <div class="value success">{{ money(stat.receivedAmount) }}<span class="unit">元</span></div>
            <div class="sub">收款记录合计</div>
          </div>
          <div class="stat-card">
            <div class="label">回款率</div>
            <div class="value primary">{{ Number(stat.receiptRate || 0).toFixed(1) }}<span class="unit">%</span></div>
            <div class="sub">已回款 / 合同金额</div>
          </div>
        </div>

        <div class="grid-2">
          <div class="chart-card">
            <div class="section-title">合同金额趋势</div>
            <div ref="lineChart" class="chart-div"></div>
          </div>
          <div class="chart-card">
            <div class="section-title">合同状态分布</div>
            <div class="donut-wrap">
              <div ref="statusChart" class="donut-div"></div>
              <div class="donut-legend">
                <div v-for="row in statusRows" :key="row.code" class="lg-row">
                  <span class="lg-dot" :style="{background: row.color}"></span>
                  <span class="lg-name">{{ row.name }}</span>
                  <span class="lg-val">{{ row.count }}</span>
                  <span class="lg-ratio">{{ row.ratio }}%</span>
                </div>
                <div v-if="!statusRows.length" class="lg-empty">暂无数据</div>
              </div>
            </div>
          </div>
        </div>

        <div class="chart-card">
          <div class="section-title">即将到期合同</div>
          <yq-table :data="stat.expiringList || []" height="auto" border stripe page-name="contractExpiring">
            <yq-table-column label="合同编号" prop="contractNumber" min-width="170rem">
              <template v-slot="{row}">
                <a @click="toDetail(row)">{{ row.contractNumber || '—' }}</a>
              </template>
            </yq-table-column>
            <yq-table-column label="合同名称" prop="contractName" min-width="200rem" :show-overflow-tooltip="true">
              <template v-slot="{row}">
                <a @click="toDetail(row)">{{ row.contractName || '—' }}</a>
              </template>
            </yq-table-column>
            <yq-table-column label="客户名称" prop="customerName" min-width="200rem" :show-overflow-tooltip="true">
              <template v-slot="{row}">{{ row.customerName || '—' }}</template>
            </yq-table-column>
            <yq-table-column label="合同金额(元)" prop="amount" width="130rem" align="right">
              <template v-slot="{row}">{{ money(row.amount) }}</template>
            </yq-table-column>
            <yq-table-column label="已回款(元)" prop="amountReceived" width="130rem" align="right">
              <template v-slot="{row}">{{ money(row.amountReceived) }}</template>
            </yq-table-column>
            <yq-table-column label="到期日期" prop="expireDate" width="120rem" align="center">
              <template v-slot="{row}">{{ dateOnly(row.expireDate) }}</template>
            </yq-table-column>
            <yq-table-column label="负责人" prop="salesName" width="110rem" align="center">
              <template v-slot="{row}">{{ row.salesName || '—' }}</template>
            </yq-table-column>
            <yq-table-column label="状态" prop="contractStatus" width="100rem" align="center">
              <template v-slot="{row}">
                <el-tag size="small" :type="contractStatusOf(row.contractStatus).type">
                  {{ contractStatusOf(row.contractStatus).text }}
                </el-tag>
              </template>
            </yq-table-column>
          </yq-table>
        </div>
      </div>
    </list-page>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import contractApi from "@/api/dz-crm/partner/contractApi";
import {useRouter} from "vue-router";
import {contractStatusOf} from "./contractDictionary";

// 状态环形图颜色：已完成绿、执行中蓝、已逾期红、草稿灰
const STATUS_COLORS = {200: "#52c41a", 100: "#1677ff", 300: "#e5484d", 0: "#909399"};

export default {
  setup() {
    return {router: useRouter()};
  },
  name: "ContractDistribution",
  components: {ListTable, ListPage},
  data() {
    return {
      loading: false,
      stat: {},
      lineChart: null,
      statusChart: null,
    };
  },
  computed: {
    statusRows() {
      return (this.stat.statusStats || []).map((row, index) => ({
        ...row,
        color: STATUS_COLORS[row.code] || ["#1677ff", "#13b8b8", "#f09819", "#7a5af8", "#909399"][index % 5],
      }));
    },
  },
  created() {
    this.loadStat();
  },
  unmounted() {
    [this.lineChart, this.statusChart].forEach(chart => chart && chart.dispose());
  },
  methods: {
    contractStatusOf,
    async loadStat() {
      this.loading = true;
      try {
        this.stat = await contractApi.statDistribution({}) || {};
        this.$nextTick(() => this.renderCharts());
      } finally {
        this.loading = false;
      }
    },
    amountWan(value) {
      return Math.round((Number(value) || 0) / 100) / 100; // 元 → 万元，保留两位
    },
    renderCharts() {
      this.lineChart = this.renderLine(this.lineChart, this.$refs.lineChart,
        this.stat.monthList || [], (this.stat.amountList || []).map(v => this.amountWan(v)));
      this.statusChart = this.renderDonut(this.statusChart, this.$refs.statusChart,
        this.statusRows, this.stat.totalCount || 0);
    },
    renderLine(chart, dom, months, amounts) {
      if (!dom) return chart;
      if (!chart) chart = markRaw(echarts.init(dom));
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true, valueFormatter: value => `${value} 万元`},
        grid: {left: "0", right: "0", bottom: "3%", top: "10%", containLabel: true},
        xAxis: {type: "category", boundaryGap: false, data: months, axisLabel: {color: "#909399"}},
        yAxis: {type: "value", name: "万元", splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
        series: [{type: "line", smooth: true, itemStyle: {color: "#1677ff"}, areaStyle: {opacity: 0.08}, data: amounts}],
      });
      return chart;
    },
    renderDonut(chart, dom, rows, centerValue) {
      if (!dom) return chart;
      if (!chart) chart = markRaw(echarts.init(dom));
      chart.setOption({
        tooltip: {trigger: "item", appendToBody: true, formatter: "{b}：{c} ({d}%)"},
        title: {
          text: String(centerValue || 0),
          subtext: "合同数量",
          left: "center",
          top: "center",
          textStyle: {fontSize: 22, fontWeight: 600, color: "#1f2329"},
          subtextStyle: {fontSize: 11, color: "#8d97a6"},
        },
        series: [{
          type: "pie",
          radius: ["58%", "80%"],
          center: ["50%", "50%"],
          avoidLabelOverlap: false,
          label: {show: false},
          labelLine: {show: false},
          itemStyle: {borderColor: "#fff", borderWidth: 2},
          data: rows.map(row => ({name: row.name, value: row.count, itemStyle: {color: row.color}})),
        }],
      });
      return chart;
    },
    toDetail(row) {
      this.router.push(`/contract/list/detail?id=${row.id}`);
    },
    money(value) {
      if (value === null || value === undefined || value === "") return "0.00";
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
  },
};
</script>

<style lang="scss" scoped>
.contract-distribution {
  height: 100%;
  .stat-body {
    padding: 10px;
  }
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 12px;
  }
  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 8px;
    padding: 16px;
    background: #fff;
    .label {
      font-size: 13px;
      color: #8d97a6;
    }
    .value {
      font-size: 24px;
      font-weight: 700;
      margin-top: 8px;
      color: #1f2329;
      &.success { color: #2e8b57; }
      &.warning { color: #f09819; }
      &.primary { color: #1677ff; }
      .unit {
        font-size: 12px;
        font-weight: 400;
        color: #8d97a6;
        margin-left: 4px;
      }
    }
    .sub {
      font-size: 12px;
      color: #909399;
      margin-top: 6px;
    }
  }
  .grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .chart-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 8px;
    padding: 14px;
    margin-bottom: 12px;
    background: #fff;
  }
  .section-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 10px;
    padding-left: 8px;
    border-left: 3px solid var(--el-color-primary);
  }
  .chart-div {
    height: 300px;
  }
  .donut-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 300px;
  }
  .donut-div {
    flex: 1;
    height: 100%;
  }
  .donut-legend {
    width: 160px;
    font-size: 12px;
  }
  .lg-row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 0;
    color: #606266;
  }
  .lg-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex: none;
  }
  .lg-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .lg-val {
    font-weight: 600;
    color: #1f2329;
  }
  .lg-ratio {
    width: 46px;
    text-align: right;
    color: #909399;
  }
  .lg-empty {
    color: #909399;
  }
}
</style>
